import { useEffect, useRef, useState } from 'react';
import { Input } from '@/shared/ui/input';
import { chercherAdresses } from '@/shared/api/geo';
import { estUnRefus } from '@/shared/api/erreurs';

// Le champ d'adresse qui PROPOSE, et qui n'empeche jamais de saisir.
//
// UN SEUL EXEMPLAIRE, POSE SUR LES QUATRE PORTES OU L'ON SAISIT UNE ADRESSE :
//
//     inst-adresse         StepClientInfo.jsx        wizard du poseur    authentifie
//     onb-adresse          OnboardingPage.js         canal QR            PUBLIC
//     new-client-adresse   ClientsPage.js            CRM, creation       authentifie
//     edit-client-adresse  ClientDetailPage.js       CRM, modification   authentifie
//
// Quatre champs ecrits separement finiraient par ne plus se comporter pareil -- et
// c'est deja arrive sur cet ecran : la recherche de PDL a ete branchee sur deux portes
// sur trois le 01/09, et la troisieme a rendu un verdict faux pendant une heure sans
// que personne ne le voie. La modification du CRM est justement celle qu'on oublie :
// elle ne ressemble pas a une creation, mais c'est la meme adresse qui part au mandat.
//
// CE QU'IL NE FAIT JAMAIS
//
// 1. Empecher de taper. C'est un `<Input>` ordinaire : la valeur saisie est TOUJOURS
//    celle du formulaire, la liste n'est qu'une aide posee a cote. La BAN tombe, la
//    saisie continue exactement comme avant ce composant.
// 2. Remplir quelque chose que l'utilisateur n'a pas choisi. Aucun choix automatique,
//    meme sur un resultat unique a 0,98 de score : l'adresse d'un mandat n'est pas un
//    endroit ou deviner.
// 3. Afficher une erreur. Une proposition qui n'aboutit pas rend une liste vide, et une
//    liste vide ne se commente pas -- l'utilisateur est en train de taper, pas de
//    lancer une recherche.
//
// LE DELAI. 300 ms apres la derniere frappe. Plus court, on interroge a chaque lettre ;
// plus long, la liste arrive apres que l'utilisateur a fini. Chaque appel part chez un
// tiers, et la route est plafonnee.
const ATTENTE_MS = 300;
const LONGUEUR_MINIMALE = 3;

// `classeChamp` habille l'INPUT, `className` le bloc qui le contient. Les deux sont
// necessaires : le champ doit garder la hauteur de ses voisins sur chaque porte (h-11
// dans le wizard du poseur, h-9 dans le CRM), et la liste doit se positionner par
// rapport au bloc. Une seule classe pour les deux ferait un champ plus petit que ceux
// d'a cote, ou une liste flottant a la mauvaise place.
export default function RechercheAdresse({
  valeur, onChange, onChoisir, placeholder = 'Commencez a taper l\'adresse…',
  className = '', classeChamp = '', testid = 'adresse', authentifie = true,
}) {
  const [propositions, setPropositions] = useState([]);
  const [ouverte, setOuverte] = useState(false);
  const dernierAppel = useRef(0);

  useEffect(() => {
    const saisie = (valeur || '').trim();
    if (saisie.length < LONGUEUR_MINIMALE) { setPropositions([]); return undefined; }
    const minuteur = setTimeout(async () => {
      const appel = ++dernierAppel.current;
      try {
        const trouvees = await chercherAdresses(saisie, { authentifie });
        // ⚠️ UNE REPONSE EN RETARD NE DOIT PAS ECRASER UNE PLUS RECENTE. Deux frappes
        // rapprochees partent en deux appels, et rien ne garantit l'ordre des retours :
        // sans ce garde, la liste affichee peut correspondre a ce qu'on tapait il y a
        // deux lettres.
        if (appel !== dernierAppel.current) return;
        setPropositions(trouvees);
      } catch (erreur) {
        // SILENCIEUX A L'ECRAN, MAIS PAS SILENCIEUX TOUT COURT.
        //
        // `frontend/CLAUDE.md` interdit qu'un ecran masque un refus : un `catch` qui
        // vide la liste affiche « aucun resultat » la ou il faudrait dire « acces
        // refuse ». La regle vaut, et pourtant ce champ-ci ne dira rien a l'utilisateur
        // dans les DEUX cas -- il est en train de taper une adresse, pas de consulter
        // une liste, et une phrase d'erreur sous le curseur serait une gene.
        //
        // Les deux echecs ne se valent pas pour autant, et confondre reste le defaut :
        // une panne de la BAN est normale et sans consequence, un 403 est un BOGUE DE
        // CABLAGE -- une porte publique branchee sur la route authentifiee. Il ne se
        // verrait jamais, puisque les deux rendent une liste vide. On le journalise
        // donc, pour le developpeur et pour la recette.
        if (estUnRefus(erreur)) {
          console.warn('[RechercheAdresse] refus de droits sur la proposition '
            + `d'adresse (authentifie=${authentifie}) : la porte est-elle branchee sur `
            + 'la bonne route ? cf. shared/api/geo.js');
        }
        setPropositions([]);
      }
    }, ATTENTE_MS);
    return () => clearTimeout(minuteur);
    // `authentifie` est dans les dependances : une porte qui bascule de public a
    // authentifie doit repartir sur la bonne route, pas garder l'ancienne.
  }, [valeur, authentifie]);

  const choisir = (proposition) => {
    setOuverte(false);
    setPropositions([]);
    onChoisir(proposition);
  };

  const visible = ouverte && propositions.length > 0;

  return (
    <div className={`relative ${className}`}>
      <Input
        className={classeChamp}
        value={valeur || ''}
        onChange={e => { onChange(e.target.value); setOuverte(true); }}
        onFocus={() => setOuverte(true)}
        // Le clic sur une proposition passe par `onMouseDown`, qui precede le blur :
        // fermer immediatement au blur avalerait le clic.
        onBlur={() => setTimeout(() => setOuverte(false), 150)}
        placeholder={placeholder}
        autoComplete="off"
        data-testid={testid}
      />
      {visible && (
        <ul
          className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-md border
                     border-neutral-200 bg-white shadow-lg"
          data-testid={`${testid}-propositions`}
        >
          {propositions.map(p => (
            <li key={p.libelle}>
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-[13px] hover:bg-neutral-50"
                onMouseDown={() => choisir(p)}
                data-testid={`${testid}-proposition`}
              >
                <span className="block">{p.adresse}</span>
                <span className="block text-[11px] text-neutral-500">
                  {p.code_postal} {p.ville}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
